import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import Subheader from "@components/Subheader/Subheader.tsx";
import { useNotification } from "@context/NotificationContext.tsx";
import type { FormContext } from "@custom-types/form-types";
import { VISIT_DETAILS_DOCUMENT_FIELDS } from "@features/port-security/constants";
import { useSecurityClearance } from "@features/port-security/hooks/useSecurityClearance.tsx";
import { getPortSecurityFormSchema } from "@features/port-security/schema/portSecurityFormSchema";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store.ts";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { usePersistentForm } from "@hooks/usePersistentForm.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { getSecurityClearanceById } from "@services/securityClearanceApi.ts";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  DATE_ONLY_FIELDS,
  formatEmiratesIdNumberValue,
  mapSecurityClearanceData,
  NO_PASSPORT_KEY,
  replaceNullsWithEmptyString,
  toDateOnlyString,
} from "./utils";

export const LANG_BASE = "security-clearance";
const TEST_ID_BASE = "security-clearance-page";

const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"] as const;

export type PortSecurityOutletContext = FormContext<PortSecurityFormData> & {
  handleSubmit: () => void;
  cancel: () => void;
  resetPersistedForm: () => void;
  submitPending: boolean;
  apiValidationErrorFields: ReadonlySet<string>;
  visaExemptNationalities: ReadonlySet<string>;
};

type NationalityReference = {
  nationality?: string;
  visaRequired?: boolean;
};

const getStoreFormValues = (): PortSecurityFormData => {
  const {
    setField: _setField,
    getField: _getField,
    setAll: _setAll,
    resetForm: _resetForm,
    ...formValues
  } = usePortSecurityFormStore.getState();
  return formValues as PortSecurityFormData;
};

const capitalizeWords = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

const SecurityClearanceContainer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const clearUploadStore = useUploadStore((state) => state.clearAll);

  const { setField, getField, setAll, resetForm } = usePortSecurityFormStore();
  const lastFullNameRef = useRef<string>("");
  const isApplyingRenewalDataRef = useRef<boolean>(false);
  const lastSignatureDateSetRef = useRef<string>("");
  const appliedRenewalDataRef = useRef<string | null>(null);
  const resetProcessedRef = useRef(false);
  const [submitPending, setSubmitPending] = useState(false);
  const [apiValidationErrorFields, setApiValidationErrorFields] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const { options: nationalities } = useFormattedReference<NationalityReference>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const visaExemptNationalities = useMemo(
    () =>
      new Set(
        nationalities
          .filter((nationality) => nationality.visaRequired === false)
          .map((nationality) => nationality.nationality?.trim())
          .filter((nationality): nationality is string => Boolean(nationality)),
      ),
    [nationalities],
  );

  const {
    securityClearanceRenewID,
    setSecurityClearanceRenewID,
    setSecurityClearanceRequestStatus,
    user,
  } = useAuthStore();

  const initialStoreValues = usePortSecurityFormStore.getState();

  const form = usePersistentForm<PortSecurityFormData>({
    schema: getPortSecurityFormSchema(t, { visaExemptNationalities }),
    defaultValues: {
      ...initialStoreValues,
    },
    storeValues: {
      ...initialStoreValues,
    },
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "security-clearance-form",
  });
  const { reset, clearErrors, watch, setError } = form;

  // Auto-populate signatureName from fullNameEn
  const fullNameEn = watch ? watch("fullNameEn") : undefined;
  const currentSignatureName = watch ? watch("signatureName") : undefined;

  useEffect(() => {
    // Skip if we're currently applying renewal data to prevent infinite loops
    if (isApplyingRenewalDataRef.current || appliedRenewalDataRef.current !== null) {
      return;
    }

    // Only update if fullNameEn exists, has changed, and signatureName doesn't already match
    if (fullNameEn && fullNameEn !== lastFullNameRef.current) {
      // Check if signatureName already matches to avoid unnecessary updates
      if (currentSignatureName !== fullNameEn) {
        lastFullNameRef.current = fullNameEn;
        setField("signatureName", fullNameEn);
        form.setValue("signatureName", fullNameEn, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      } else {
        // Update ref even if we don't set the value to prevent re-running
        lastFullNameRef.current = fullNameEn;
      }
    } else if (!fullNameEn) {
      // Reset the ref when fullNameEn is cleared
      lastFullNameRef.current = "";
    }
  }, [fullNameEn, currentSignatureName, setField, form]);

  // Auto-populate signatureDate with today's date
  const currentSignatureDate = watch ? watch("signatureDate") : undefined;
  useEffect(() => {
    // Skip if we're currently applying renewal data to prevent infinite loops
    if (isApplyingRenewalDataRef.current || appliedRenewalDataRef.current !== null) {
      return;
    }

    const today = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD

    // Skip if we've already set the date to today in this session
    if (lastSignatureDateSetRef.current === today) {
      return;
    }

    // Check both form value and store value to avoid unnecessary updates
    const storeSignatureDate = getField("signatureDate");
    const formSignatureDate = currentSignatureDate || storeSignatureDate;

    // Only update if the current date is different from today
    if (formSignatureDate !== today) {
      lastSignatureDateSetRef.current = today;
      setField("signatureDate", today);
      form.setValue("signatureDate", today, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      // Update ref even if we don't set the value to prevent re-running
      lastSignatureDateSetRef.current = today;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSignatureDate, setField, form]);

  const clearForm = useCallback(() => {
    // Reset the ref so auto-population works after reset
    lastFullNameRef.current = "";
    resetForm();
    const emptyValues = getStoreFormValues();
    reset(emptyValues);
  }, [resetForm, reset]);

  const repopulateIndividualFromUser = useCallback(() => {
    if (securityClearanceRenewID) {
      return;
    }
    const roles = user?.roles ?? [];
    const isIndividual = INDIVIDUAL_ROLES.some((role) => roles.includes(role));
    if (!isIndividual) {
      return;
    }

    const tokenEmiratesId = String(user?.emiratesId ?? "").trim();
    if (tokenEmiratesId) {
      const currentEmiratesId = String(form.getValues("emiratesIdNumber") ?? "").trim();
      if (!currentEmiratesId) {
        const formatted = formatEmiratesIdNumberValue(tokenEmiratesId);
        if (formatted) {
          setField("emiratesIdNumber", formatted);
          form.setValue("emiratesIdNumber", formatted, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }
      }
    }

    const userFullName = user?.name ?? "";
    const isUaeUser = roles.includes("uae-pass-user");

    if (!userFullName || !isUaeUser) {
      return;
    }

    const capitalizedName = capitalizeWords(userFullName);
    // Force the signature sync effect after we set fullNameEn
    lastFullNameRef.current = "";
    setField("fullNameEn", capitalizedName);
    form.setValue("fullNameEn", capitalizedName, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [user, setField, form, securityClearanceRenewID]);

  const updateFormData: PortSecurityOutletContext["updateFormData"] = useCallback(
    (key, value) => {
      const typedKey = key as keyof PortSecurityFormData;
      const typedValue = value as PortSecurityFormData[keyof PortSecurityFormData];

      const normalizedValue = DATE_ONLY_FIELDS.has(typedKey)
        ? toDateOnlyString(typedValue as Date | string | null | undefined)
        : typedValue;
      setField(typedKey, normalizedValue as PortSecurityFormData[typeof typedKey]);

      // Drop stale server/API step errors once the user edits the field.
      setApiValidationErrorFields((prev) => {
        if (!prev.has(String(typedKey))) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(String(typedKey));
        return next;
      });
      clearErrors(typedKey);
    },
    [setField, clearErrors],
  );

  const clearVisitDetailsDocumentFormValues = useCallback(() => {
    VISIT_DETAILS_DOCUMENT_FIELDS.forEach((field) => {
      form.setValue(field, [] as PortSecurityFormData[typeof field], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    });
  }, [form]);

  const { onSubmit } = useSecurityClearance({
    formValues: usePortSecurityFormStore.getState(),
    resetForm: clearForm,
    clearVisitDetailsDocumentFormValues,
    setError,
    onSubmitError: () => setSubmitPending(false),
    onApiValidationErrors: (fields) => {
      setApiValidationErrorFields(new Set(fields.map(String)));
    },
  });

  const onValid = async (validatedFormValues: PortSecurityFormData) => {
    if (useUploadStore.getState().hasAnyUploadInFlight()) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    setSubmitPending(true);
    await onSubmit(validatedFormValues);
  };

  const onInvalid = () => {
    // this runs when zod validation fails
    notify({
      title: t("security-clearance.notifications.sc-request-submitted-error-title"),
      description: t("security-clearance.notifications.sc-request-submitted-error-description"),
      type: "error",
    });
  };

  const handleSubmit = () => {
    localStorage.removeItem(NO_PASSPORT_KEY);
    setApiValidationErrorFields(new Set());

    form.handleSubmit(onValid, onInvalid)();
  };

  useEffect(() => {
    return () => {
      setSecurityClearanceRenewID(null);
      setSecurityClearanceRequestStatus(null);
    };
  }, []);

  const cancel = () => {
    clearForm();
    navigate(`/${paths.portSecurity.index}`);
    clearUploadStore();
    setSecurityClearanceRenewID(null);
  };

  const location = useLocation();

  const { data } = useQuery({
    queryKey: ["security-clearance-renewal-form", securityClearanceRenewID],
    queryFn: async () => {
      const res = await getSecurityClearanceById({
        id: securityClearanceRenewID!,
        service: "bff-gatepass",
      });
      return res;
    },
    enabled: !!securityClearanceRenewID,
  });

  const applySecurityClearancePreview = useCallback(
    (previewData: any) => {
      // Set flags to prevent useEffect from running during renewal data loading
      isApplyingRenewalDataRef.current = true;
      appliedRenewalDataRef.current = securityClearanceRenewID || "unknown";

      const mappedData = mapSecurityClearanceData(previewData, t);
      const cleanedData = replaceNullsWithEmptyString(mappedData);

      // Always populate signatureName from fullNameEn during renewal
      if (cleanedData.fullNameEn) {
        cleanedData.signatureName = cleanedData.fullNameEn;
        // Update ref to prevent useEffect from firing when flag is cleared
        lastFullNameRef.current = cleanedData.fullNameEn;
      }

      // Always set signatureDate to today during renewal to prevent effect from running
      const today = new Date().toISOString().slice(0, 10);
      cleanedData.signatureDate = today;
      lastSignatureDateSetRef.current = today;

      setAll(cleanedData);
      reset(cleanedData);
    },
    [t, setAll, reset, securityClearanceRenewID],
  );

  useEffect(() => {
    if (data) {
      applySecurityClearancePreview(data);
      // Clear flags after a delay to ensure all updates are processed
      // The delay ensures that all effects triggered by form.reset have completed
      const timeoutId1 = setTimeout(() => {
        isApplyingRenewalDataRef.current = false;
      }, 300);

      // Clear applied renewal data ref after a bit more time
      // This ensures effects won't run during the renewal data application
      const timeoutId2 = setTimeout(() => {
        appliedRenewalDataRef.current = null;
      }, 500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }
  }, [data, applySecurityClearancePreview]);

  // Reset applied renewal data ref when renew ID changes or form is cleared
  useEffect(() => {
    if (!securityClearanceRenewID) {
      appliedRenewalDataRef.current = null;
      isApplyingRenewalDataRef.current = false;
    }
  }, [securityClearanceRenewID]);

  useEffect(() => {
    const locationState = location.state as { resetSCForm?: boolean } | undefined;
    const resetFlag = locationState?.resetSCForm;

    // Only process if flag is explicitly true
    if (resetFlag !== true) {
      resetProcessedRef.current = false;
      return;
    }

    // Avoid re-processing the same reset
    if (resetProcessedRef.current) {
      return;
    }

    resetProcessedRef.current = true;

    // Preserve passport/emirates ID values that were entered in the ID checker
    const currentPassport = getField("passportNumber");
    const currentEmiratesId = getField("emiratesIdNumber");

    // Clear the form
    clearForm();

    // Repopulate passport/emirates ID if they were set
    if (currentPassport) {
      setField("passportNumber", currentPassport);
      form.setValue("passportNumber", currentPassport, { shouldDirty: true });
    }
    if (currentEmiratesId) {
      setField("emiratesIdNumber", currentEmiratesId);
      form.setValue("emiratesIdNumber", currentEmiratesId, { shouldDirty: true });
    }

    // Repopulate individual defaults from token / profile if applicable
    repopulateIndividualFromUser();

    // Navigate once to remove the flag and allow future resets
    navigate(location.pathname, {
      replace: true,
      state: { ...(locationState ?? {}), resetSCForm: false },
    });
  }, [location, getField, setField, form, clearForm, repopulateIndividualFromUser, navigate]);

  const outletCtx: PortSecurityOutletContext = {
    formData: form,
    updateFormData,
    resetPersistedForm: clearForm,
    handleSubmit,
    cancel,
    submitPending,
    apiValidationErrorFields,
    visaExemptNationalities,
    clearErrors: clearErrors,
    setError,
    watch,
  };

  return (
    <div data-testid={TEST_ID_BASE}>
      <Subheader
        title={t(`${LANG_BASE}.request-security-clearance`)}
        subtitle={t(`${LANG_BASE}.request-security-clearance`)}
      />
      <Outlet context={outletCtx} />
    </div>
  );
};

export default SecurityClearanceContainer;
